'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface TimelineStep {
  id: number;
  phase: string;
  title: string;
  duration: string;
  description: string;
  activities: string[];
  icon: string;
}

const journeySteps: TimelineStep[] = [
  {
    id: 1,
    phase: 'Phase 1',
    title: 'Program Enrollment & Assessment',
    duration: 'Week 1',
    description: 'Begin your journey with a comprehensive skills assessment and personalized learning path creation.',
    activities: [
      'Complete initial skills assessment',
      'Meet with career advisor',
      'Set career goals and timeline',
      'Access learning resources'
    ],
    icon: 'AcademicCapIcon'
  },
  {
    id: 2,
    phase: 'Phase 2',
    title: 'Skill Development & Projects',
    duration: 'Weeks 2-16',
    description: 'Build real-world skills through hands-on projects and expert mentorship.',
    activities: [
      'Complete structured curriculum',
      'Build portfolio projects',
      'Participate in code reviews',
      'Attend live workshops'
    ],
    icon: 'CodeBracketIcon'
  },
  {
    id: 3,
    phase: 'Phase 3',
    title: 'Resume & Profile Optimization',
    duration: 'Weeks 14-16',
    description: 'Transform your professional presence with expert guidance on resume and LinkedIn optimization.',
    activities: [
      'Professional resume review',
      'LinkedIn profile enhancement',
      'Portfolio website creation',
      'Personal branding strategy'
    ],
    icon: 'DocumentTextIcon'
  },
  {
    id: 4,
    phase: 'Phase 4',
    title: 'Interview Preparation',
    duration: 'Weeks 16-18',
    description: 'Master interview skills through mock interviews and personalized feedback from industry professionals.',
    activities: [
      'Technical interview practice',
      'Behavioral question coaching',
      'System design discussions',
      'Salary negotiation prep'
    ],
    icon: 'UserGroupIcon'
  },
  {
    id: 5,
    phase: 'Phase 5',
    title: 'Job Search & Applications',
    duration: 'Weeks 18-22',
    description: 'Access exclusive job opportunities and receive direct referrals to partner companies.',
    activities: [
      'Apply to curated job listings',
      'Receive company referrals',
      'Track application progress',
      'Schedule interviews'
    ],
    icon: 'BriefcaseIcon'
  },
  {
    id: 6,
    phase: 'Phase 6',
    title: 'Offer & Onboarding Support',
    duration: 'Weeks 22-24',
    description: 'Negotiate your best offer and transition smoothly into your new role with ongoing support.',
    activities: [
      'Evaluate job offers',
      'Negotiate compensation',
      'Accept ideal position',
      'Onboarding guidance'
    ],
    icon: 'CheckBadgeIcon'
  }
];

export default function CareerJourneyTimeline() {
  const [activeStep, setActiveStep] = useState<number>(1);

  return (
    <section className="py-16 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-headline font-bold text-foreground mb-4">
            Your Career Transformation Journey
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Follow our proven 6-phase process from enrollment to job placement. Each phase is designed to build your skills and confidence systematically.
          </p>
        </div>

        {/* Desktop Timeline */}
        <div className="hidden lg:block">
          <div className="relative">
            <div className="absolute top-20 left-0 right-0 h-1 bg-gradient-to-r from-primary via-accent to-sky-light" />

            <div className="grid grid-cols-6 gap-4">
              {journeySteps.map((step, index) => (
                <div
                  key={step.id}
                  className="relative cursor-pointer"
                  onMouseEnter={() => setActiveStep(step.id)}
                >
                  <div className="flex flex-col items-center">
                    <div
                      className={`w-16 h-16 rounded-full flex items-center justify-center transition-all duration-300 ${
                        activeStep === step.id
                          ? 'bg-gradient-to-br from-primary to-accent shadow-sky-md scale-110'
                          : 'bg-card border-2 border-primary/30'
                      }`}
                    >
                      <Icon
                        name={step.icon as any}
                        size={28}
                        variant="solid"
                        className={activeStep === step.id ? 'text-primary-foreground' : 'text-primary'}
                      />
                    </div>

                    <div className="mt-4 text-center" style={ {marginTop: '2rem'} }>
                      <div className="text-xs font-headline font-semibold text-primary mb-1">
                        {step.phase}
                      </div>
                      <div className={`text-sm font-headline font-bold transition-colors duration-300 ${
                        activeStep === step.id ? 'text-foreground' : 'text-muted-foreground'
                      }`}>
                        {step.title}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {step.duration}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-12 bg-card rounded-xl p-8 shadow-sky-sm border border-border">
            {journeySteps.map((step) => (
              <div
                key={step.id}
                className={`transition-all duration-300 ${
                  activeStep === step.id ? 'block' : 'hidden'
                }`}
              >
                <div className="flex items-start space-x-4 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-primary to-accent rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon name={step.icon as any} size={24} variant="solid" className="text-primary-foreground" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-2xl font-headline font-bold text-foreground">
                        {step.title}
                      </h3>
                      <span className="px-3 py-1 bg-primary/10 text-primary text-sm font-headline font-semibold rounded-full">
                        {step.duration}
                      </span>
                    </div>
                    <p className="text-muted-foreground">
                      {step.description}
                    </p>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  {step.activities.map((activity, idx) => (
                    <div key={idx} className="flex items-start space-x-3 p-4 bg-primary/5 rounded-lg">
                      <Icon name="CheckCircleIcon" size={20} variant="solid" className="text-success mt-0.5 flex-shrink-0" />
                      <span className="text-foreground">{activity}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Mobile Timeline */}
        <div className="lg:hidden space-y-6">
          {journeySteps.map((step, index) => (
            <div
              key={step.id}
              className="bg-card rounded-xl p-6 shadow-sky-sm border border-border"
            >
              <div className="flex items-start space-x-4 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-primary to-accent rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name={step.icon as any} size={24} variant="solid" className="text-primary-foreground" />
                </div>
                <div className="flex-1">
                  <div className="text-xs font-headline font-semibold text-primary mb-1">
                    {step.phase}
                  </div>
                  <h3 className="text-lg font-headline font-bold text-foreground mb-1">
                    {step.title}
                  </h3>
                  <div className="text-sm text-muted-foreground">
                    {step.duration}
                  </div>
                </div>
              </div>

              <p className="text-sm text-muted-foreground mb-4">
                {step.description}
              </p>

              <div className="space-y-2">
                {step.activities.map((activity, idx) => (
                  <div key={idx} className="flex items-start space-x-2">
                    <Icon name="CheckCircleIcon" size={16} variant="solid" className="text-success mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-foreground">{activity}</span>
                  </div>
                ))}
              </div>

              {index < journeySteps.length - 1 && (
                <div className="flex justify-center mt-4">
                  <Icon name="ChevronDownIcon" size={24} variant="outline" className="text-primary" />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}